import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getToken, setActiveTownId } from "../api";
import { useSession } from "../stores/session";

type PublicTown = {
  id: string;
  name: string;
  slug: string;
  status: string;
  timeScale: number;
  residentCount: number;
  memberCount: number;
  ownerUsername: string | null;
  announcement: string;
  isFull: boolean;
  canEnter: boolean;
};

const STATUS = {
  active: { label: "活跃", className: "bg-emerald-50 text-emerald-700" },
  slow: { label: "慢速中", className: "bg-amber-50 text-amber-700" },
  sleeping: { label: "沉睡中", className: "bg-slate-100 text-slate-600" },
  closed: { label: "已关闭", className: "bg-red-50 text-red-700" },
} as const;

const RELOCATION_MESSAGE: Record<string, string> = {
  guest_not_supported: "游客身份不能搬家，注册账号后即可迁居。",
  source_not_found: "没有找到你当前居住的小镇，请先重新进入小镇。",
  source_missing_agent: "你还没有自己的角色，请先在原镇创建角色。",
  target_not_found: "目标小镇不存在或已经关闭。",
  same_town: "你已经住在这座小镇。",
  target_banned: "你已被目标小镇封禁，无法搬过去。",
  target_sleeping: "目标小镇正在沉睡，暂时无法搬入。",
  target_closed: "目标小镇暂不接收新居民。",
  target_full: "目标小镇已经满员。",
  target_has_agent: "你在目标小镇已经有一个角色。",
};

export default function Halls() {
  const [towns, setTowns] = useState<PublicTown[]>([]);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState("");
  const [error, setError] = useState("");
  const nav = useNavigate();
  const { user, loading: sessionLoading, refresh } = useSession();

  useEffect(() => {
    api.towns()
      .then((result) => setTowns(result.towns || []))
      .catch((reason) => setError(reason.message || "小镇列表加载失败"))
      .finally(() => setLoading(false));
  }, []);

  const enter = async (town: PublicTown) => {
    if (!getToken()) {
      nav(`/auth?next=${encodeURIComponent(`/halls/${town.id}`)}`);
      return;
    }
    setEntering(town.id);
    setError("");
    try {
      const needsRelocation = !!user?.homeTownId && user.homeTownId !== town.id;
      if (needsRelocation) {
        const previewResult = await api.relocationPreview(town.id);
        const preview = previewResult.preview;
        if (!preview?.canMove) {
          setError(RELOCATION_MESSAGE[preview?.reason] || "当前不能搬到这座小镇。");
          return;
        }
        if (!window.confirm(`确认搬到「${town.name}」？你的角色和私有记忆会随迁，原镇公开历史保留。`)) return;
        await api.relocateToTown(town.id);
        setActiveTownId(town.id);
        await refresh();
        nav(`/town/${town.id}`);
        return;
      }

      const result = await api.enterTown(town.id);
      setActiveTownId(town.id);
      const nextUser = await refresh();
      if (result.hasAgent || nextUser?.agent) nav(`/town/${town.id}`);
      else nav(`/create-agent?townId=${encodeURIComponent(town.id)}`);
    } catch (reason: any) {
      if (reason.code === "already_has_home") {
        const homeTownName = reason.data?.homeTownName
          || towns.find((town) => town.id === reason.data?.homeTownId)?.name
          || "原来的小镇";
        setError(`你已在「${homeTownName}」生活，跨镇搬家暂未开放。`);
      } else if (reason.code === "banned") {
        setError("你已被该小镇封禁，无法进入。");
      } else if (reason.code === "town_sleeping") {
        setError("小镇关上门睡觉了，暂时无法进入。");
      } else {
        setError(reason.message || "进入失败");
      }
    } finally {
      setEntering("");
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#f5f2e8] text-[#28251f]">
      <header className="border-b border-[#ddd7c4] bg-[#18352f] text-white">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-4">
          <Link to="/" className="text-lg font-black">星原小镇</Link>
          <span className="text-sm text-[#a9c4ba]">小镇大厅</span>
          <div className="flex-1" />
          {user ? (
            <>
              <span className="hidden text-sm text-[#c8d8d2] sm:inline">{user.username}</span>
              <Link to="/town" className="rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">进入我的小镇</Link>
            </>
          ) : (
            <Link to="/auth" className="rounded-lg bg-[#e8c96a] px-4 py-1.5 text-sm font-bold text-[#1d1d1d]">登录 / 注册</Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-black">选择一座小镇开始生活</h1>
          <p className="mt-2 text-sm text-[#77705f]">每座镇有独立的居民、记忆、时间流速和经济系统。</p>
        </div>
        {error && <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {loading && <div className="py-20 text-center text-sm text-[#77705f]">正在寻找开放的小镇...</div>}
        {!loading && towns.length === 0 && <div className="py-20 text-center text-sm text-[#77705f]">暂无开放小镇。</div>}

        <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {towns.map((town) => {
            const status = STATUS[town.status as keyof typeof STATUS] || STATUS.closed;
            const isHome = user?.homeTownId === town.id;
            const canRelocate = !!user?.homeTownId && !isHome;
            const disabled = !town.canEnter || town.status === "sleeping" || (town.isFull && !isHome);
            return (
              <article key={town.id} className="min-w-0 rounded-xl border border-[#ddd7c4] bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-[#e8e2cd] text-xl">🏘️</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate font-black">{town.name}</h2>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${status.className}`}>{status.label}</span>
                    </div>
                    <div className="mt-1 text-xs text-[#77705f]">镇长/城主：{town.ownerUsername || "平台直营"}</div>
                  </div>
                </div>
                <p className="mt-4 min-h-[40px] text-sm leading-6 text-[#5f594d]">{town.announcement || "欢迎来到新的小镇生活。"}</p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs text-[#665f4f]">
                  <span className="rounded-md bg-[#f2efe4] px-2 py-1">{town.timeScale}x 时间流速</span>
                  <span className="rounded-md bg-[#f2efe4] px-2 py-1">{town.residentCount} 位居民</span>
                  {town.isFull && <span className="rounded-md bg-amber-50 px-2 py-1 text-amber-700">已满员</span>}
                  {isHome && <span className="rounded-md bg-emerald-50 px-2 py-1 text-emerald-700">当前居住</span>}
                </div>
                <button
                  onClick={() => enter(town)}
                  disabled={sessionLoading || disabled || entering === town.id}
                  className="mt-5 w-full rounded-lg bg-[#2f6e5b] py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-[#aaa698]"
                >
                  {entering === town.id
                    ? "处理中..."
                    : town.status === "sleeping"
                      ? "小镇睡着了"
                      : isHome
                        ? "进入小镇"
                        : town.isFull
                          ? "居民已满"
                          : canRelocate
                            ? "搬到这里"
                            : "进入小镇"}
                </button>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
